// /admins — gestión de superadmins del sistema (users.role='superadmin').
//
// Modelo:
//   - `users.role='superadmin'` = personas que operan TODOS los clientes.
//     Pueden tener un cliente de casa (`users.tenant`) sin perder el resto.
//   - `users.role='client_admin'` = admins de UN cliente específico. Se
//     invitan desde /clientes → editar tenant → "Invitar admin". NO se
//     gestionan acá.
//   - `_superusers` (admins nativos de PB) = solo para acceso al panel
//     técnico api.lifefocus.agency/_/. Daniel debería ser el único.
//     Tampoco se gestionan acá.
//
// Visible solo para usuarios con role === 'superadmin' actualmente logueados.
// Reusa _authed.clientes.module.css para mantener consistencia visual con el
// resto del panel superadmin (mismo header, tabla, botones, modales).

import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import {
  UserPlus,
  ShieldAlert,
  Trash2,
  KeyRound,
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { pb, currentUser } from '~/lib/pb';
import { formatPbError } from '~/lib/pb-error';
import { useTenants } from '~/lib/tenants-queries';
import { forceLogout } from '~/lib/session';
import { PasswordInput } from '~/components/PasswordInput/PasswordInput';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import styles from './_authed.clientes.module.css';

interface SuperadminUser {
  id: string;
  email: string;
  name: string;
  display_name: string;
  role: 'superadmin';
  tenant?: string;
  created: string;
  updated: string;
}

export const Route = createFileRoute('/_authed/admins')({
  component: AdminsPage,
});

function AdminsPage() {
  const me = currentUser();
  const isSuperadmin = me?.role === 'superadmin';
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [passwordModal, setPasswordModal] = useState<SuperadminUser | null>(null);

  const { data: admins = [], isLoading } = useQuery({
    queryKey: ['superadmin-users'],
    queryFn: async (): Promise<SuperadminUser[]> => {
      const res = await pb.collection('users').getList<SuperadminUser>(1, 200, {
        filter: 'role = "superadmin"',
        sort: 'created',
      });
      return res.items;
    },
    enabled: isSuperadmin,
  });

  if (!isSuperadmin) {
    return (
      <div className={styles.page}>
        <div className={styles.notSuperadmin}>
          <ShieldAlert size={32} aria-hidden style={{ marginBottom: 12 }} />
          <p>La gestión de administradores es solo para superadmins.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="h2">Administradores</h1>
          <p className="page-header__subtitle">
            El cliente de casa los muestra en el equipo y en Asignado de ese cliente, y les manda
            sus avisos. En los demás clientes siguen como superadmin.
          </p>
        </div>
        <div className="page-header__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setCreateOpen(true)}
          >
            <UserPlus size={14} aria-hidden />
            Crear administrador
          </button>
        </div>
      </header>

      {isLoading && <div className="empty-state">Cargando administradores…</div>}

      {!isLoading && admins.length === 0 && (
        <div className="empty-state">
          No hay administradores. Crea el primero con el botón arriba.
        </div>
      )}

      {!isLoading && admins.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Administrador</th>
              <th>Cliente de casa</th>
              <th>Desde</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => {
              const isMe = a.id === me?.id;
              const displayName =
                a.display_name || a.name || a.email.split('@')[0];
              const showDisplayName =
                displayName && displayName !== a.email.split('@')[0];
              return (
                <tr key={a.id}>
                  <td>
                    <div className={styles.tenantCell}>
                      <div className={styles.tenantCell__icon}>
                        <KeyRound size={14} aria-hidden />
                      </div>
                      <div>
                        <div className={styles.tenantCell__name}>
                          <span>{a.email}</span>
                          {isMe && (
                            <span
                              className={styles.statusInactive}
                              style={{ marginLeft: 8 }}
                            >
                              tú
                            </span>
                          )}
                        </div>
                        {showDisplayName && (
                          <div className={styles.tenantCell__slug}>
                            {displayName}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <HomeTenantSelect admin={a} />
                  </td>
                  <td>
                    <span className={styles.statusInactive}>
                      {new Date(a.created).toLocaleDateString('es-CL', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() => setPasswordModal(a)}
                        title="Cambiar contraseña"
                        aria-label={`Cambiar contraseña de ${a.email}`}
                      >
                        <KeyRound size={14} aria-hidden />
                      </button>
                      {!isMe && (
                        <DeleteAdminButton
                          admin={a}
                          onDeleted={() =>
                            qc.invalidateQueries({ queryKey: ['superadmin-users'] })
                          }
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {createOpen && (
        <CreateAdminModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['superadmin-users'] });
            setCreateOpen(false);
          }}
        />
      )}

      {passwordModal && (
        <ChangePasswordModal
          admin={passwordModal}
          onClose={() => setPasswordModal(null)}
        />
      )}
    </div>
  );
}

function HomeTenantSelect({ admin }: { admin: SuperadminUser }) {
  const qc = useQueryClient();
  const tenantsQ = useTenants();
  const [value, setValue] = useState(admin.tenant || '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(admin.tenant || '');
  }, [admin.id, admin.tenant]);

  const save = useMutation({
    mutationFn: async (tenantId: string) => {
      await pb.collection('users').update(admin.id, { tenant: tenantId });
    },
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['superadmin-users'] });
    },
    onError: (err) => setError(formatPbError(err, 'No se pudo guardar el cliente de casa.')),
  });

  const tenants = tenantsQ.data ?? [];
  const known = !value || tenants.some((t) => t.id === value);

  return (
    <div>
      <select
        className={styles.select}
        aria-label={`Cliente de casa de ${admin.email}`}
        value={value}
        disabled={save.isPending || tenantsQ.isLoading}
        onChange={(e) => {
          const next = e.target.value;
          setValue(next);
          setError(null);
          save.mutate(next);
        }}
      >
        <option value="">Sin cliente de casa</option>
        {!known ? <option value={value}>Cliente guardado</option> : null}
        {tenants.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
      {error ? <p className={styles.error}>{error}</p> : null}
    </div>
  );
}

// ── Crear admin ────────────────────────────────────────────────────────

function CreateAdminModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      if (password !== passwordConfirm) {
        throw new Error('Las contraseñas no coinciden.');
      }
      if (password.length < 10) {
        throw new Error('La contraseña debe tener al menos 10 caracteres.');
      }
      const trimmedEmail = email.trim().toLowerCase();
      const baseName = name.trim() || trimmedEmail.split('@')[0];
      return await pb.collection('users').create({
        email: trimmedEmail,
        password,
        passwordConfirm,
        name: baseName,
        display_name: baseName,
        role: 'superadmin',
        emailVisibility: true,
        verified: true,
      });
    },
  });

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync();
      onCreated();
    } catch (err) {
      setError(formatPbError(err, 'No se pudo crear el administrador.'));
    }
  }

  return (
    <div className={styles.modalBackdrop} onClick={onClose} role="presentation">
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className={styles.modal__header}>
          <h2>Crear administrador</h2>
          <button
            type="button"
            className={styles.iconButton}
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={16} aria-hidden />
          </button>
        </header>
        <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
          <div className={styles.modal__body}>
            <p style={{ color: 'var(--color-text-subtle)', margin: 0, fontSize: 'var(--font-size-body-sm)' }}>
              El nuevo admin verá <strong>todos los clientes</strong> y operará
              el sistema completo, igual que tú. El cliente de casa se elige
              después, en esta lista.
            </p>
            <div className={styles.field}>
              <label className={styles.field__label}>Email</label>
              <input
                type="email"
                className={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="christian@lifefocus.agency"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.field__label}>
                Nombre para mostrar (opcional)
              </label>
              <input
                type="text"
                className={styles.input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Christian Pérez"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.field__label}>Contraseña</label>
              <PasswordInput
                className={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={10}
                placeholder="Mínimo 10 caracteres"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.field__label}>Confirmar contraseña</label>
              <PasswordInput
                className={styles.input}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
                minLength={10}
              />
            </div>
            {error && (
              <p className={styles.error}>
                <AlertCircle size={14} aria-hidden style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
                {error}
              </p>
            )}
          </div>
          <div className={styles.modal__footer}>
            <div className={styles.modal__footerLeft} />
            <div className={styles.modal__footerRight}>
              <button
                type="button"
                className={styles.buttonGhost}
                onClick={onClose}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={styles.button}
                disabled={create.isPending}
              >
                {create.isPending ? (
                  <>
                    <Loader2 size={14} aria-hidden /> Creando…
                  </>
                ) : (
                  <>
                    <UserPlus size={14} aria-hidden /> Crear
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Cambiar contraseña ─────────────────────────────────────────────────

function ChangePasswordModal({
  admin,
  onClose,
}: {
  admin: SuperadminUser;
  onClose: () => void;
}) {
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const update = useMutation({
    mutationFn: async () => {
      if (password !== passwordConfirm) {
        throw new Error('Las contraseñas no coinciden.');
      }
      if (password.length < 10) {
        throw new Error('La contraseña debe tener al menos 10 caracteres.');
      }
      return await pb
        .collection('users')
        .update(admin.id, { password, passwordConfirm });
    },
  });

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await update.mutateAsync();
      if (admin.id === currentUser()?.id) {
        forceLogout('password_changed');
        return;
      }
      setSaved(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(formatPbError(err, 'No se pudo actualizar la contraseña.'));
    }
  }

  return (
    <div className={styles.modalBackdrop} onClick={onClose} role="presentation">
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className={styles.modal__header}>
          <h2>Cambiar contraseña</h2>
          <button
            type="button"
            className={styles.iconButton}
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={16} aria-hidden />
          </button>
        </header>
        <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
          <div className={styles.modal__body}>
            <p style={{ color: 'var(--color-text-subtle)', margin: 0, fontSize: 'var(--font-size-body-sm)' }}>
              Vas a cambiar la contraseña de <strong>{admin.email}</strong>.
            </p>
            <div className={styles.field}>
              <label className={styles.field__label}>Nueva contraseña</label>
              <PasswordInput
                className={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={10}
                autoFocus
              />
            </div>
            <div className={styles.field}>
              <label className={styles.field__label}>
                Confirmar nueva contraseña
              </label>
              <PasswordInput
                className={styles.input}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
                minLength={10}
              />
            </div>
            {error && (
              <p className={styles.error}>
                <AlertCircle size={14} aria-hidden style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
                {error}
              </p>
            )}
            {saved && (
              <p
                style={{
                  padding: 'var(--space-3)',
                  background: 'rgba(34, 139, 84, 0.08)',
                  border: '1px solid rgba(34, 139, 84, 0.2)',
                  borderRadius: 'var(--radius-md)',
                  color: 'rgb(34, 139, 84)',
                  fontSize: 'var(--font-size-body-sm)',
                  margin: 0,
                }}
              >
                <CheckCircle2 size={14} aria-hidden style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
                Contraseña actualizada.
              </p>
            )}
          </div>
          <div className={styles.modal__footer}>
            <div className={styles.modal__footerLeft} />
            <div className={styles.modal__footerRight}>
              <button
                type="button"
                className={styles.buttonGhost}
                onClick={onClose}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={styles.button}
                disabled={update.isPending}
              >
                {update.isPending ? (
                  <>
                    <Loader2 size={14} aria-hidden /> Guardando…
                  </>
                ) : (
                  <>
                    <KeyRound size={14} aria-hidden /> Cambiar
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Borrar admin ───────────────────────────────────────────────────────

function DeleteAdminButton({
  admin,
  onDeleted,
}: {
  admin: SuperadminUser;
  onDeleted: () => void;
}) {
  const confirm = useConfirm();
  const del = useMutation({
    mutationFn: async () => pb.collection('users').delete(admin.id),
    onSuccess: onDeleted,
  });

  return (
    <button
      type="button"
      className={styles.iconButton}
      title="Eliminar"
      aria-label={`Eliminar ${admin.email}`}
      disabled={del.isPending}
      onClick={async () => {
        const ok = await confirm({
          title: 'Eliminar administrador',
          description: (
            <>
              ¿Eliminar a <strong>{admin.email}</strong>? Perderá acceso al
              sistema. Esta acción no se puede deshacer.
            </>
          ),
          confirmLabel: 'Eliminar',
          tone: 'danger',
        });
        if (!ok) return;
        del.mutate();
      }}
    >
      <Trash2 size={14} aria-hidden />
    </button>
  );
}
